import { flushSync } from 'react-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useRecordStore from 'contexts/record-store';
import React from 'react';
import { Types } from 'utils/constants';
import { useSnackbar } from 'notistack';
import Card from 'components/card';
import Icon from 'components/icon';

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: theme.spacing(40),
    backgroundColor: 'transparent',
    '& .MuiDialogActions-spacing > :not(:first-child)': {
      [theme.breakpoints.down('sm')]: {
        marginLeft: '0px',
      },
    },
  },
  titleIcon: {
    marginRight: theme.spacing.dialogTitleIconMargin,
  },
  message: {
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    minWidth: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      minHeight: theme.spacing(6),
    },
  },
}));

const DeleteCompleteAnswerDialog = ({ exerciseId, onClose, setIsLoading }) => {
  const classes = useStyles();
  const recordStore = useRecordStore();
  const { enqueueSnackbar } = useSnackbar();
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId);
  const answerRecords = recordStore.useRecordArray(Types.Record.ANSWER, exerciseRecord?.get()?.answers);
  const [open, setOpen] = React.useState(true);
  const completedAnswers = answerRecords.filter(a => a.get().dog.appliesToExercise && !!a.get().dateCompleted);
  const initialState = completedAnswers.map(a => ({
    id: a.get().id,
    dog: a.get().dog.name,
    checked: false
  }));
  const [selectedAnswers, setSelectedAnswers] = React.useState(initialState);


  if (!exerciseRecord || completedAnswers.length === 0) {
    return (<></>);
  }

  const handleChange = (answerId, event) => {
    const { checked } = event.target;
    setSelectedAnswers(prev => prev.map(a => {
      if (a.id === answerId) {
        return {
          ...a,
          checked
        }
      }

      return {...a};
    }));
  }

  const onCancel = () => {
    setOpen(false);
    setTimeout(() => onClose(), 100);
  }

  const onSubmit = async () => {
    setIsLoading(true);
    setOpen(false);

    try {
      const selectedAnswerIds = selectedAnswers.filter(a => a.checked).map(a => a.id);
      await recordStore.deleteCompletedAnswers(exerciseId, selectedAnswerIds);
      enqueueSnackbar (`Completion record${selectedAnswerIds.length > 1 ? 's' : ''} deleted`, {
        variant: 'success'
      });

    } catch (error) {
      let errmsg = 'Error deleting completion records';
      if (error.response && error.response.status === 403) {
        errmsg = 'You are not authorized to edit this exercise';
      }

      enqueueSnackbar(errmsg, {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });

    } finally {
      flushSync(() => {
        setIsLoading(false);
      });
      onClose();
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onCancel}
        classes={{paper: classes.root}}
        aria-labelledby="delete-complete-answer-title"
        aria-describedby="delete-complete-answer-content" >
        <Card
          titleStyle="error"
          title={<>
            <Icon className={classes.titleIcon} type="exercise"/>
            Delete K9 Completion Record
          </>}
          id="delete-complete-answer-title" >
          <Box>
            <DialogContent id="delete-complete-answer-content">
              <DialogContentText className={classes.message}>
                Select the K9 completion record(s) to delete for exercise "{exerciseRecord.get().name}". This action cannot be undone.
              </DialogContentText>

              <FormControl component="fieldset">
                <FormGroup>
                  { selectedAnswers.map(ans => (
                    <FormControlLabel
                      key={ans.id}
                      control={<Checkbox
                                  color="primary"
                                  checked={ans.checked}
                                  name={`${ans.id}-checkbox`}
                                  onChange={(e) => handleChange(ans.id, e) } />}
                      label={ans.dog}
                     />
                  ))}
                </FormGroup>
              </FormControl>
            </DialogContent>
          </Box>
          <DialogActions>
            <Button
              className={classes.actionButton}
              onClick={onCancel}>
                Cancel
            </Button>
            <Button
              color="primary"
              className={classes.actionButton}
              disabled={!selectedAnswers.find(a => a.checked)}
              onClick={onSubmit}>
                Delete
            </Button>
          </DialogActions>
        </Card>
      </Dialog>
    </>
  )
}

export default DeleteCompleteAnswerDialog;